/**
 * What the UI needs to know about the outbox, as one observable value.
 *
 * Written by the outbox and the flusher; read by `useOfflineStatus` through
 * `useSyncExternalStore`, so the snapshot is replaced, never mutated.
 * `version` changes on every outbox mutation, even one that leaves the
 * counts alone (a coalesce, a queued→inflight transition), so a live list can
 * re-read.
 */

export interface OutboxState {
  pendingCount: number;
  failedCount: number;
  /** Replay hit a 401: the store is kept, the user must sign in to sync. */
  needsSignIn: boolean;
  /** No Web Locks in this browser and rows are waiting: they will never replay here. */
  locksUnavailable: boolean;
  syncing: boolean;
  lastSyncAt: number | null;
  /** Bumped on every outbox mutation. */
  version: number;
}

const initial: OutboxState = {
  pendingCount: 0,
  failedCount: 0,
  needsSignIn: false,
  locksUnavailable: false,
  syncing: false,
  lastSyncAt: null,
  version: 0,
};

let state: OutboxState = initial;
const listeners = new Set<() => void>();

/** The current snapshot (stable reference until the next change). */
export function getOutboxState(): OutboxState {
  return state;
}

/** Tell every subscriber the snapshot moved. */
function notify(): void {
  listeners.forEach(listener => {
    try {
      listener();
    } catch {
      // One broken subscriber must not stop the others.
    }
  });
}

/** Replace part of the state; no-op, and no notification, when nothing changes. */
export function patchOutboxState(patch: Partial<OutboxState>): void {
  const next = { ...state, ...patch };
  if ((Object.keys(next) as (keyof OutboxState)[]).every(k => next[k] === state[k])) return;
  state = next;
  notify();
}

/** Back to the initial snapshot; called on sign-out and wipe. */
export function resetOutboxState(): void {
  state = { ...initial };
  notify();
}

/** Be told when the state changes. Returns a disposer. */
export function subscribeToOutboxState(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Tests start idle with no subscribers. */
export function resetOutboxStateForTests(): void {
  state = initial;
  listeners.clear();
}
